import type { ReactElement } from "react";
import type { LiquidityAccount, LiquidityPosition, LiquidityPositionDetail, Unternehmen } from "@kundeninfo/api-client";
import { Icon, useSofortPopup } from "@kundeninfo/ui";
import { Chip, Knopf, Objekt, Schalter, statusRang } from "@kundeninfo/ui/me-design";
import type { ObjektProps, ObjektStatusArt } from "@kundeninfo/ui/me-design";
import { ErsteSpalte, objektFuss } from "../../components/objektPanel/ErsteSpalte.js";
import type { ObjektKnoepfe } from "../../components/objektPanel/ErsteSpalte.js";
import { useKennungsAttribut } from "../../components/objektPanel/kennungsAttribut.js";
import { kennzeichenFolge, kennzeichenTeil, unternehmenKuerzel } from "../../components/objektPanel/kennzeichen.js";
import { bezugAusAkteZiel } from "../../components/objektPanel/objektBezug.js";
import type { ObjektVerweisProps } from "../../components/objektPanel/ObjektVerweis.js";
import { useObjektPanel } from "../../components/objektPanel/ObjektPanelKontext.js";
import { formatDatum } from "../../format.js";
import { cents } from "../suppliers/shared.js";
import { euro, positionKey } from "./liquidityModel.js";
import type { DraftChange } from "./liquidityModel.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import type { Detail, PlanningIntent } from "./LiquidityPositions.js";
import { AmountEnd, DatedAmountRow, PlanningAmountList } from "./PlanningItemPanel.js";
import type { PlanningAmount } from "./PlanningItemPanel.js";

/**
 * DIE LIQUIDITÄTSPOSITION ALS `Objekt` („Öffnen statt Springen“ Aufgabe 4; Spec „Umstellung auf Objekt“, K1–K4, F15, P8) — die EINE
 * Zeile der Positionsspalte, für Aufträge, Verträge, Lieferanten, Planpositionen, Konten und Konten ohne Position. Die Teile ordnet
 * `positionTeile` zu. Vorn Klapp-Pfeil und Schalter „einbeziehen“ (nur im editierbaren Szenario), im Fuß der Verweis (Objektpanel
 * bzw. eigenes Panel der Liquidität; Sprung in die Akte), Einfrieren, „⋯“; aufgeklappt die Detailzeilen (115): Posten, Planbeträge
 * oder Kontostände. Das Symbol nach der Quelle (Entscheid 116, überholt Spec F25 „kein `art`“).
 */

/** Eine Position der Reihe oder ein Konto ohne Position (inaktiv, ohne Stand im Horizont): dasselbe Objekt (PF13). */
export type PositionQuelle = LiquidityPosition | { konto: LiquidityAccount };

export type PositionTeile = Pick<ObjektProps, "name" | "bezug" | "art" | "wert" | "status" | "statusArt">
  & { kennzeichen: ReturnType<typeof kennzeichenFolge>; angaben: string[] };

/** Die Zeile der Quelle: `companyId|ref`, für Konten ohne Position `companyId|ACCOUNT:id` — derselbe Schlüssel wie mit Position. */
export const quellenSchluessel = (x: PositionQuelle): string => ("konto" in x ? `${x.konto.companyId}|ACCOUNT:${x.konto.id}` : positionKey(x));

/** Die Stellen, an die der Fokus nach dem Neuladen zurückgeht (Spec F9, Review Focus 2): Wirte mit `focus()` auf ihren Knopf (W-8). */
export const ANKER = {
  objekt: (key: string): string => `[data-position-key="${key}"]`,
  verweis: (key: string): string => `[data-position-key="${key}"] > [slot="fuss"] me-verweis`,
  /** Das „⋯“ steht im Fuß immer zuletzt (Verweis → Umschalter → „⋯“). */
  menue: (key: string): string => `[data-position-key="${key}"] > [slot="fuss"] > me-knopf:last-of-type`,
  betrag: (amountId: string): string => `[data-amount-id="${amountId}"] me-knopf`,
  /** Das „⋯“ am Posten einer Rechnung („Erwartungstermin ändern …“). */
  rechnung: (invoiceId: string): string => `[data-invoice-id="${invoiceId}"] me-knopf`,
  /** Der Verweis am Posten (F9: nach dem Schreiben im Panel der Rechnung bzw. Eingangsrechnung), nie das „⋯“ daneben. */
  posten: (attribut: "data-invoice-id" | "data-incoming-invoice-id", id: string): string => `[${attribut}="${id}"] me-verweis`,
} as const;

/** Die Objektart je Quelle (Entscheid 116): Symbol und Ton aus me-design; ein Konto ohne Position ist ein Konto. */
const ART: Readonly<Record<LiquidityPosition["kind"], NonNullable<ObjektProps["art"]>>> = {
  ACCOUNT: "konto", PLANNING_ITEM: "planposition", SALES_ORDER: "auftrag", SUPPLIER_CONTRACT: "vertrag", SUPPLIER: "lieferant",
};

/** Getönte Hinweise (PF18): der schlechteste ist der Status, die übrigen stehen neutral bei den Kennzeichen. */
const GETOENT: Readonly<Partial<Record<LiquidityPosition["notes"][number], ObjektStatusArt>>> = { OVERDUE: "fehler", STALE_BALANCE: "warnung" };

const balanceText = (konto: LiquidityAccount | undefined): string => {
  const b = konto?.balances[0];
  return b === undefined ? T.positions.noBalance : T.positions.balanceOf(euro(Number(cents(b.amount))), formatDatum(b.balanceDate));
};

/**
 * Die Teile nach Anatomie 1.2: Name Bezeichnung, Bezug Kunde/Lieferant/IBAN, Art nach der Quelle (116), Wert Summe der Position; Kennzeichen (112)
 * Unternehmen (nur bei mehreren in der Sicht) → „nicht eingerechnet“, „eingefroren …“ (nur ohne Bearbeitung: sonst zeigen Schalter
 * und Einfrieren den Stand) → „im Entwurf geändert“ → Marken. Überfällig, Kontostand veraltet, Quelle entfernt: der schlechteste nach
 * status.js ist der Status (PF18, wie 705/712 — Abweichung von der Spec-Tabelle), die übrigen bleiben neutral, nichts geht verloren.
 * Angaben: Stand des Kontos, „nächster …“.
 */
export function positionTeile(x: PositionQuelle, o: { sitzung: readonly Unternehmen[]; mehrereUnternehmen: boolean; editierbar: boolean;
  aenderung: DraftChange | undefined; konto?: LiquidityAccount }): PositionTeile {
  const unternehmen = (companyId: string): string[] => unternehmenKuerzel(companyId, o.sitzung, o.mehrereUnternehmen);
  if ("konto" in x) {
    const a = x.konto;
    return { name: a.name, ...(a.iban === null ? {} : { bezug: a.iban }), art: ART.ACCOUNT,
      kennzeichen: kennzeichenFolge({ unternehmen: unternehmen(a.companyId), hinweise: a.active ? [] : [{ text: T.positions.inactive }] }, o.sitzung),
      angaben: [balanceText(a)] };
  }
  const getoent = [
    ...x.notes.flatMap((n) => { const art = GETOENT[n]; return art === undefined ? [] : [{ text: T.note[n], art }]; }),
    ...(x.sourceGone ? [{ text: T.note.sourceGone, art: "warnung" as const }] : []),
  ];
  const status = getoent.reduce<(typeof getoent)[number] | undefined>((s, h) => (s === undefined || statusRang(h.art) < statusRang(s.art) ? h : s), undefined);
  const hinweise = [
    ...(!o.editierbar && !x.enabled ? [T.note.disabled] : []),
    ...(!o.editierbar && x.frozen !== null ? [T.note.frozen(formatDatum(x.frozen.at), formatDatum(x.frozen.to))] : []),
    ...(o.aenderung === undefined ? [] : [T.positions.changedInDraft]),
    ...x.notes.filter((n) => GETOENT[n] === undefined).map((n) => T.note[n]),
    ...getoent.filter((h) => h !== status).map((h) => h.text),
    ...(x.scenarioOnly ? [T.note.scenarioOnly] : []),
  ];
  return {
    name: x.label, ...(x.detail === null ? {} : { bezug: x.detail }), art: ART[x.kind], wert: euro(x.inCents + x.outCents),
    ...(status === undefined ? {} : { status: status.text, statusArt: status.art }),
    kennzeichen: kennzeichenFolge({ unternehmen: unternehmen(x.companyId), hinweise: hinweise.map((text) => ({ text })) }, o.sitzung),
    angaben: [...(x.kind === "ACCOUNT" ? [balanceText(o.konto)] : []),
      ...(x.next === null ? [] : [T.positions.next(formatDatum(x.next.date), euro(x.next.cents))])],
  };
}

/** Einfrieren (`snowflake`), gerahmt und gedrückt gefüllt (PF16, Anatomie Teil 14). Das Sofort-Popup nennt den Stand. */
function Einfrieren(p: { position: LiquidityPosition; change: boolean | undefined; gesperrt: boolean; onToggle: () => void }): ReactElement {
  const { frozen, label } = p.position;
  const text = p.change === true ? T.positions.freezeOnSave : p.change === false ? T.positions.liveOnSave
    : frozen === null ? T.positions.freezeOf(label) : T.note.frozen(formatDatum(frozen.at), formatDatum(frozen.to));
  const popup = useSofortPopup<HTMLElement>(text);
  return (
    <Knopf ref={popup} art="sekundaer" kompakt gedrueckt={p.change ?? frozen !== null} deaktiviert={p.gesperrt} aria-label={T.positions.freezeOf(label)}
      onClick={p.onToggle}>
      <Icon name="snowflake" size={12} />
    </Knopf>
  );
}

/** Was ein Aufklapper geladen hat: bis dahin „Lädt …“, ein Fehlschlag steht in der Zeile. */
function Loaded(p: { detail: Detail | undefined; children: (detail: LiquidityPositionDetail) => ReactElement }): ReactElement {
  if (p.detail === undefined || p.detail.state === "loading") return <p className="liq__muted" role="status">{T.loading}</p>;
  if (p.detail.state === "failed") return <p className="liq__muted">{T.positions.loadFailed}</p>;
  return p.children(p.detail.detail);
}

type Post = LiquidityPositionDetail["posts"][number];

/** Der Bezug eines Postens auf seinen Beleg: Rechnung (Panel ab Stufe 3, `kannOeffnen`) bzw. Eingangsrechnung; nie ein Sprung (115). */
function postBezug(post: Post, x: LiquidityPosition): ObjektVerweisProps["bezug"] | null {
  const art = post.sourceType === "INVOICE" ? "rechnung" : post.sourceType === "INCOMING_INVOICE" ? "eingangsrechnung" : null;
  if (art === null || post.sourceId === null || x.target === null) return null;
  return { art, id: post.sourceId, customerId: x.target.customerId, companyId: x.companyId, label: post.reference ?? "" };
}

/** Die Posten als einzeilige Detailzeilen (115): erste Spalte mit Verweis (nur Panel) und „⋯“, Betrag rechts ohne Knopf. */
function Posts(p: { position: LiquidityPosition; posts: readonly Post[]; onErwartungstermin: (invoiceId: string) => void;
  onGeaendert: (anker: string) => void }): ReactElement {
  if (p.posts.length === 0) return <p className="liq__muted">{T.positions.noPosts}</p>;
  return (
    <ul className="liq__posts">
      {p.posts.map((post, k) => {
        const invoiceId = post.sourceType === "INVOICE" ? post.sourceId : null;
        const incomingId = post.sourceType === "INCOMING_INVOICE" ? post.sourceId : null;
        const bezug = postBezug(post, p.position);
        return (
          <li key={invoiceId === null ? `${post.sourceType}|${post.sourceId ?? k}|${post.expectedDate}` : `INVOICE|${invoiceId}`} className="liq__post"
            data-invoice-id={invoiceId ?? undefined} data-incoming-invoice-id={incomingId ?? undefined}>
            <ErsteSpalte {...(bezug === null ? {} : { verweis: { bezug, ohneSprung: true,
              onGeaendert: () => p.onGeaendert(ANKER.posten(incomingId === null ? "data-invoice-id" : "data-incoming-invoice-id", bezug.id)) } })}
              {...(invoiceId === null ? {} : { menue: { name: T.expectedPayment.postActionsOf(post.reference),
                eintraege: [{ wert: "expected", text: T.expectedPayment.changeFromPost }], onWahl: () => p.onErwartungstermin(invoiceId) } })} />
            <span className="liq__post-text">
              {post.reference !== null && <span>{post.reference}</span>}
              <span>{T.positions.expectedAt(formatDatum(post.expectedDate))}</span>
              {post.expected !== null && <span>{T.reason[post.expected.reason]}</span>}
              {post.dueDate !== null && <span className="liq__muted">{T.positions.dueAt(formatDatum(post.dueDate))}</span>}
            </span>
            {post.overdue && <Chip statusArt="fehler">{T.note.OVERDUE}</Chip>}
            {post.projected && <Chip>{T.note.PROJECTED}</Chip>}
            <AmountEnd amount={euro(post.cents)} />
          </li>
        );
      })}
    </ul>
  );
}

/** Die Stände eines Kontos stehen in `getLiquidityAccounts`: kein Abruf, und ein inaktives Konto hat keine Position (404). */
function Balances(p: { account: LiquidityAccount | undefined }): ReactElement {
  const balances = p.account?.balances ?? [];
  return (
    <div>
      <p className="liq__posts-title">{T.positions.balances}</p>
      {balances.length === 0 ? <p className="liq__muted">{T.positions.noBalance}</p> : (
        <ul className="liq__posts">
          {balances.map((b) => <DatedAmountRow key={b.id} label={formatDatum(b.balanceDate)} note={b.note} amount={euro(Number(cents(b.amount)))} />)}
        </ul>
      )}
    </div>
  );
}

/** Die Beträge einer Planposition ab `validFrom`; Löschen leise am Zeilenende (115, Entscheid 43), der früheste bleibt. */
function PlanningAmounts(p: { amounts: LiquidityPositionDetail["amounts"]; onDelete: (a: PlanningAmount) => void }): ReactElement {
  return (
    <div className="liq__amounts">
      <p className="liq__posts-title">{T.planningItem.amounts}</p>
      <PlanningAmountList amounts={p.amounts ?? []} locked={false} onDelete={p.onDelete} />
    </div>
  );
}

export type LiquidityPositionObjektProps = {
  x: PositionQuelle;
  /** Schalter und Einfrieren: nur bei genau einem Unternehmen mit Basis (593, P6). */
  editierbar: boolean;
  /** Der Entwurf wird gespeichert: Schalter und Einfrieren schalten nicht. */
  gesperrt: boolean;
  aufgeklappt: boolean;
  onAufgeklappt: (offen: boolean) => void;
  onEinbeziehen: () => void;
  onEinfrieren: () => void;
  onPlanung: (i: PlanningIntent) => void;
  onKonto: (a: "edit" | "balance") => void;
  onErwartungstermin: (invoiceId: string) => void;
  details: Detail | undefined;
  aenderung: DraftChange | undefined;
  /** Das Konto einer Kontoposition: Stand in den Angaben, Kontostände als Detailzeilen. */
  konto?: LiquidityAccount;
  companies: readonly Unternehmen[];
  mehrereUnternehmen: boolean;
  /** Im Objektpanel wurde geschrieben (F9): mit der Stelle, an die der Fokus nach dem Neuladen zurückgeht (`ANKER`). */
  onGeaendert: (anker: string) => void;
};

export function LiquidityPositionObjekt(p: LiquidityPositionObjektProps): ReactElement {
  const wege = useObjektPanel();
  const key = quellenSchluessel(p.x);
  const merke = useKennungsAttribut("data-position-key", key);
  const position = "konto" in p.x ? null : p.x;
  const konto = "konto" in p.x ? p.x.konto : p.konto;
  const { kennzeichen, angaben, ...teile } = positionTeile(p.x, { sitzung: p.companies, mehrereUnternehmen: p.mehrereUnternehmen,
    editierbar: p.editierbar, aenderung: p.aenderung, ...(konto === undefined ? {} : { konto }) });
  const art = position === null || position.kind === "ACCOUNT" ? "konto"
    // Eine eingefrorene Zeile, deren Planposition entfernt ist, hat nichts mehr zu ändern: Sie steht nur noch als Stand da (F6).
    : position.kind === "PLANNING_ITEM" && !position.sourceGone ? "planung" : "beleg";

  /** Verweis und „⋯“ (F15, P8): Konto und Planposition öffnen ihr eigenes Panel, die übrigen das Objektpanel bzw. springen. */
  const knoepfe = ((): ObjektKnoepfe => {
    if (position === null || art === "konto") {
      const id = konto?.id ?? position?.id;
      const name = teile.name ?? "";
      return {
        ...(id === undefined ? {} : { verweis: { bezug: { art: "konto", id, label: name }, eigenesPanel: () => p.onKonto("edit") } }),
        menue: { name: T.positions.accountActionsOf(name), eintraege: [{ wert: "balance", text: T.positions.addBalance }], onWahl: () => p.onKonto("balance") },
      };
    }
    if (art === "planung") {
      return {
        verweis: { bezug: { art: "planposition", id: position.id, label: position.label }, eigenesPanel: () => p.onPlanung({ kind: "edit", position }) },
        menue: { name: T.planningItem.actionsOf(position.label), eintraege: [{ wert: "changeAmount", text: T.planningItem.changeAmount }],
          onWahl: () => p.onPlanung({ kind: "changeAmount", position }) },
      };
    }
    return position.target === null ? {}
      : { verweis: { bezug: bezugAusAkteZiel(position.target, position.label), onGeaendert: () => p.onGeaendert(ANKER.verweis(key)) } };
  })();
  const fuss = objektFuss(wege, { ...knoepfe, ...(p.editierbar && position !== null && art !== "konto"
    ? { umschalter: <Einfrieren position={position} change={p.aenderung?.freeze} gesperrt={p.gesperrt} onToggle={p.onEinfrieren} /> } : {}) });

  const details = (): ReactElement => {
    if (position === null || art === "konto") return <Balances account={konto} />;
    return (
      <Loaded detail={p.details}>
        {(d) => (art === "planung"
          ? <PlanningAmounts amounts={d.amounts} onDelete={(amount) => p.onPlanung({ kind: "deleteAmount", position, amount })} />
          : <Posts position={position} posts={d.posts} onErwartungstermin={p.onErwartungstermin} onGeaendert={p.onGeaendert} />)}
      </Loaded>
    );
  };

  return (
    <Objekt ref={merke} {...teile} klappbar aufgeklappt={p.aufgeklappt} onAufgeklappt={(d) => p.onAufgeklappt(d.aufgeklappt)}
      {...(p.editierbar && position !== null ? { schalter: (
        <Schalter aria-label={T.positions.enabledOf(position.label)} an={p.aenderung?.enabled ?? position.enabled} gesperrt={p.gesperrt}
          onGeaendert={p.onEinbeziehen} />
      ) } : {})}
      kennzeichen={kennzeichenTeil(kennzeichen)} fuss={fuss} angaben={angaben.join(" · ")}>
      {p.aufgeklappt && details()}
    </Objekt>
  );
}
